import { Goal } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/i18n/LanguageContext';
import { calculateDaysRemainingStr, getGoalProgress, getGoalStatus } from '@/lib/utils.ts';
import { format } from 'date-fns';
import { AlertCircle, Calendar, CheckCircle2, Target, XCircle } from 'lucide-react';

interface GoalDetailsProps {
  goal: Goal;
  onEdit: () => void;
  onDelete: () => void;
}

const getStatusIcon = (status: 'onTrack' | 'atRisk' | 'behind') => {
  switch (status) {
    case 'onTrack':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'atRisk':
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    case 'behind':
      return <XCircle className="h-5 w-5 text-red-500" />;
    default:
      return <AlertCircle className="h-5 w-5 text-gray-500" />;
  }
};

const getPriorityColor = (priority: string): string => {
  const colors: Record<string, string> = {
    mandatory: '#D32F2F',
    'nice-to-have': '#1976D2',
  };
  return colors[priority] || '#757575';
};

export const GoalDetails = ({ goal, onEdit, onDelete }: GoalDetailsProps) => {
  const { t } = useLanguage();
  const status = getGoalStatus(goal);
  const remainingAmount = goal.targetAmount - goal.currentAmount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-primary/10 mr-3 rounded p-2">
            <Target className="h-5 w-5" style={{ color: getPriorityColor(goal.priority) }} />
          </div>
          <div>
            <h3 className="text-lg font-medium">{goal.name}</h3>
            <p className="text-muted-foreground text-sm">
              {t(`goals.priorities.${goal.priority}`)}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          <Button variant="destructive" onClick={onDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>

      <Separator />

      <Card>
        <CardHeader>
          <CardTitle>{t('goals.progress')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t('goals.progress')}</span>
            <span className="text-sm font-medium">{getGoalProgress(goal).toFixed(1)}%</span>
          </div>
          <Progress value={getGoalProgress(goal)} className="h-2" />
          <div className="flex items-center justify-between text-sm">
            <span>
              {goal.currentAmount.toLocaleString()} {goal.currency}
            </span>
            <span>
              {goal.targetAmount.toLocaleString()} {goal.currency}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>{t('goals.remainingAmount')}</span>
            <span>
              {remainingAmount.toLocaleString()} {goal.currency}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>{t('goals.daysRemaining')}</span>
            <span>{calculateDaysRemainingStr(goal.dueDate)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>{t('goals.status')}</span>
            <div className="flex items-center">
              {getStatusIcon(status)}
              <span className="ml-1">{t(`goals.status.${status}`)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('goals.details.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {goal.description && (
            <div>
              <h4 className="text-sm font-medium">{t('goals.description')}</h4>
              <p className="text-muted-foreground text-sm">{goal.description}</p>
            </div>
          )}
          <div>
            <h4 className="text-sm font-medium">{t('goals.dueDate')}</h4>
            <div className="text-muted-foreground flex items-center text-sm">
              <Calendar className="mr-1 h-4 w-4" />
              <span>{format(new Date(goal.dueDate), 'PPP')}</span>
            </div>
          </div>
          {goal.linkedAccountId && (
            <div>
              <h4 className="text-sm font-medium">{t('goals.linkedAccount')}</h4>
              <p className="text-muted-foreground text-sm">{goal.linkedAccountId}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
